import * as THREE from 'three'
import { DioramaEnvironment } from './DioramaEnvironment'
import { cloneDioramaTerrainMaterial, fittedDioramaRock, projectDioramaTerrainUVs } from './DioramaTerrain'
import { DioramaStaticBatch } from './DioramaStaticBatch'
import { dioramaArt } from './DioramaArt'
import { storyPropsOwnLegacy } from './DioramaStoryProps'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import type { ActionCue, Beat, Character, DioramaProp, SceneArt } from '../data/cutscenes'
import { getLevelTheme, type LevelTheme } from '../data/level-themes'
import { ADAPTIVE_QUALITY_TIERS, type AdaptiveQualityTier } from '../engine/AdaptiveQuality'
import { buildKrabbyPatty, buildThemeProp } from '../procedural/models'
import type { CollisionBody } from './CollisionGuard'

interface RestTransform {
  position: THREE.Vector3
  rotation: THREE.Euler
  scale: THREE.Vector3
}

interface PropRig {
  root: THREE.Object3D
  rests: Map<THREE.Object3D, RestTransform>
}

const SCENE_LEVEL: Record<SceneArt, number> = {
  town: 1,
  krabbyshack: 1,
  jellytrail: 1,
  coralcave: 2,
  seaweed: 3,
  bubblebridge: 4,
  ship: 5,
  deepsea: 6,
  pattyreef: 7,
  storm: 8,
  royalcave: 9,
  throne: 10,
  reopening: 1,
}

export const DIORAMA_LEGACY_DETAIL_BUDGET = {
  backdropProps: 14,
  bubbles: 36,
  total: 50,
} as const

/** Three times the former ambient population: +200 percent visible detail at tier 4. */
export const DIORAMA_HIGH_DETAIL_BUDGET = {
  backdropProps: 42,
  bubbles: 108,
  total: 150,
} as const

const FLOOR_SCATTER_COUNT = 48
const BACKDROP_RELIEF_COUNT = DIORAMA_HIGH_DETAIL_BUDGET.backdropProps - DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps

/** Overscan both backdrop cards so the portrait camera never reveals a card edge. */
export const DIORAMA_BACKDROP_SIZE = {
  back: { width: 42, height: 26 },
  mid: { width: 38, height: 22 },
} as const

export interface DioramaDetailStats {
  tier: AdaptiveQualityTier
  capacityMultiplier: number
  maxElements: number
  visibleElements: number
  instancedDrawCalls: number
}

export function dioramaDetailStatsForTier(tier: AdaptiveQualityTier): DioramaDetailStats {
  const fraction = ADAPTIVE_QUALITY_TIERS[tier].detailFraction
  const visibleBackdrop = tier === 0
    ? Math.ceil(DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps / 2)
    : Math.max(DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps, Math.round(DIORAMA_HIGH_DETAIL_BUDGET.backdropProps * fraction))
  const visibleBubbles = Math.max(DIORAMA_LEGACY_DETAIL_BUDGET.bubbles, Math.round(DIORAMA_HIGH_DETAIL_BUDGET.bubbles * fraction))
  const visibleFloor = Math.max(12, Math.round(FLOOR_SCATTER_COUNT * fraction))
  return {
    tier,
    capacityMultiplier: DIORAMA_HIGH_DETAIL_BUDGET.total / DIORAMA_LEGACY_DETAIL_BUDGET.total,
    maxElements: DIORAMA_HIGH_DETAIL_BUDGET.total + FLOOR_SCATTER_COUNT,
    visibleElements: visibleBackdrop + visibleBubbles + visibleFloor,
    instancedDrawCalls: 3,
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function ease(value: number): number {
  const t = clamp01(value)
  return t * t * (3 - 2 * t)
}

function captureRest(root: THREE.Object3D): Map<THREE.Object3D, RestTransform> {
  const rests = new Map<THREE.Object3D, RestTransform>()
  root.traverse((object) => {
    rests.set(object, {
      position: object.position.clone(),
      rotation: object.rotation.clone(),
      scale: object.scale.clone(),
    })
  })
  return rests
}

function restore(rests: Map<THREE.Object3D, RestTransform>): void {
  for (const [object, rest] of rests) {
    object.position.copy(rest.position)
    object.rotation.copy(rest.rotation)
    object.scale.copy(rest.scale)
  }
}

export class DioramaSet {
  readonly root = new THREE.Group()
  readonly theme: LevelTheme
  private props = new Map<DioramaProp, PropRig>()
  private beat: Beat
  private ownedProps: ReadonlySet<DioramaProp> = new Set()
  private locationProps = new Set<DioramaProp>()
  private ambientBackdropProps: THREE.Object3D[] = []
  private ownedGeometries = new Set<THREE.BufferGeometry>()
  private ownedMaterials = new Set<THREE.Material>()
  private materialCache = new Map<string, THREE.Material>()
  private detailTier: AdaptiveQualityTier = 4
  private backdropRelief: THREE.InstancedMesh
  private floorScatter: THREE.InstancedMesh
  private bubbles: THREE.InstancedMesh
  private accentLight: THREE.PointLight
  private actionRing: THREE.Mesh
  private environment!: DioramaEnvironment
  private backdropBatch!: DioramaStaticBatch
  private seabedRelief: THREE.DataTexture | null = null
  private terrainTextures: THREE.Texture[] = []
  private readonly causticTime = { value: 0 }

  constructor(readonly sceneId: SceneArt, beat: Beat) {
    this.beat = beat
    this.theme = getLevelTheme(SCENE_LEVEL[sceneId])
    this.root.name = `DioramaSet_${sceneId}`
    this.buildPlatform()
    this.floorScatter = this.buildFloorScatter()
    this.buildBackdrop()
    this.backdropRelief = this.buildBackdropRelief()
    this.buildLocation()
    this.bubbles = this.buildBubbles()
    this.actionRing = this.buildActionRing()
    this.actionRing.visible = false
    this.accentLight = new THREE.PointLight(dioramaArt(sceneId).accent, 0.24, 8, 2)
    this.accentLight.position.set(0, 3.4, 1.4)
    this.root.add(this.accentLight)
    this.setDetailTier(4)
    this.setBeat(beat)
  }

  setBeat(beat: Beat): void {
    this.beat = beat
    this.ownedProps = new Set()
    const needed = new Set(this.locationProps)
    for (const cue of beat.actions) if (cue.prop) needed.add(cue.prop)
    for (const prop of needed) {
      if (!this.props.has(prop) && !storyPropsOwnLegacy(beat, prop)) this.addProp(prop)
    }
    for (const rig of this.props.values()) restore(rig.rests)
    this.actionRing.visible = false
    this.refreshPropVisibility()
  }

  setOwnedProps(props: ReadonlySet<DioramaProp>): void {
    this.ownedProps = props
    this.refreshPropVisibility()
  }

  private refreshPropVisibility(): void {
    for (const [prop, rig] of this.props) {
      const needed = this.locationProps.has(prop) || this.beat.actions.some(cue => cue.prop === prop)
      rig.root.visible = needed && !storyPropsOwnLegacy(this.beat, prop) && !this.ownedProps.has(prop)
    }
  }

  get detailStats(): DioramaDetailStats {
    return dioramaDetailStatsForTier(this.detailTier)
  }

  collisionBodies(): CollisionBody[] {
    return [...this.props.entries()]
      .filter(([, rig]) => rig.root.visible)
      .map(([prop, rig]) => {
        // The clam shell is a wide visual silhouette, but its collision volume
        // is the lower shell/hinge footprint. Using the full shell AABB made a
        // character standing beside it register as inside the open lid.
        if (prop === 'clam') rig.root.userData.collisionProxy = {
          center: [0, 1.05, -0.08],
          halfSize: [1.28, 1.05, 0.82],
        }
        if (prop === 'jelly-jars') rig.root.userData.collisionProxy = {
          center: [0, 0.62, 0],
          halfSize: [0.62, 0.62, 0.42],
        }
        if (prop === 'decorations') rig.root.userData.collisionProxy = {
          center: [0, 0.58, 0],
          halfSize: [0.92, 0.58, 0.42],
        }
        if (prop === 'sandwich') rig.root.userData.collisionProxy = {
          center: [0, 0.18, 0],
          halfSize: [0.42, 0.18, 0.34],
        }
        if (prop === 'coin-chest') rig.root.userData.collisionProxy = {
          center: [0, 0.78, 0],
          halfSize: [0.92, 0.78, 0.58],
        }
        return {
          object: rig.root,
          name: rig.root.name,
          kind: 'set-prop' as const,
          purpose: `animated ${prop} set prop`,
        }
      })
  }

  setDetailTier(tier: AdaptiveQualityTier): void {
    this.detailTier = tier
    const stats = dioramaDetailStatsForTier(tier)
    const visibleBackdrop = tier === 0
      ? Math.ceil(DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps / 2)
      : Math.max(
        DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps,
        Math.round(DIORAMA_HIGH_DETAIL_BUDGET.backdropProps * ADAPTIVE_QUALITY_TIERS[tier].detailFraction),
      )
    for (const [index, prop] of this.ambientBackdropProps.entries()) {
      prop.visible = index < Math.min(visibleBackdrop, DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps)
    }
    this.backdropBatch.setVisibleProps(Math.min(visibleBackdrop, DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps))
    this.backdropRelief.count = Math.max(0, visibleBackdrop - DIORAMA_LEGACY_DETAIL_BUDGET.backdropProps)
    this.bubbles.count = Math.max(
      DIORAMA_LEGACY_DETAIL_BUDGET.bubbles,
      Math.round(DIORAMA_HIGH_DETAIL_BUDGET.bubbles * ADAPTIVE_QUALITY_TIERS[tier].detailFraction),
    )
    this.floorScatter.count = stats.visibleElements - visibleBackdrop - this.bubbles.count
    this.root.userData.detailStats = stats
  }

  update(
    beat: Beat,
    progress: number,
    elapsed: number,
    actorPosition: (character: Character) => THREE.Vector3,
  ): void {
    if (beat !== this.beat) this.setBeat(beat)
    for (const prop of this.props.values()) restore(prop.rests)
    this.refreshPropVisibility()
    this.actionRing.visible = false
    this.causticTime.value = elapsed
    this.environment.update(elapsed)
    this.bubbles.rotation.y = elapsed * 0.035
    this.bubbles.position.y = Math.sin(elapsed * 0.35) * 0.12
    this.accentLight.intensity = 0.24 + Math.sin(elapsed * 1.7) * 0.025

    for (const cue of beat.actions) {
      const start = cue.delay ?? 0
      const cueProgress = clamp01((progress - start) / Math.max(0.001, 1 - start))
      if (cueProgress <= 0) continue
      this.applyAction(cue, cueProgress, elapsed, actorPosition)
    }
    if (beat.shot === 't4-2a-02') {
      // This guard strike must remove the crossing Bob is actually testing,
      // not an unrelated decorative bubble behind him. Rest restoration above
      // also makes scrubbing back before the strike deterministic.
      const victim = this.props.get('bridge')?.root.getObjectByName('BridgeBubble_3')
      if (victim) victim.scale.multiplyScalar(Math.max(0.001, 1 - clamp01((progress - 0.44) / 0.08)))
    }
    const clam = this.props.get('clam')
    if (clam && ['t6-2a-01', 't7-2a-02'].includes(beat.shot)) {
      const lid = clam.root.getObjectByName('ClamUpper')!
      lid.rotation.x = beat.shot === 't6-2a-01'
        ? -1.1 * (1 - clamp01((progress - 0.48) / 0.18))
        : beat.shot === 't7-2a-02' ? -1.1 * (1 - clamp01((progress - 0.72) / 0.15)) : 0
      if (beat.shot === 't6-2a-01') clam.root.position.z = -1.6
    }
    this.enforcePropGroundContacts()
  }

  /** Dynamic set props are authored from local motion curves. Resolve their
   * final world bounds against the location's surface so collapse/drop clips
   * stop on the floor rather than tunnelling below it. The ship and bridge have
   * deliberately lower deck/channel surfaces than the shared acting corridor. */
  private enforcePropGroundContacts(): void {
    const surfaceY = this.sceneId === 'bubblebridge' ? -0.9 : this.sceneId === 'ship' ? -0.25 : -0.02
    for (const { root } of this.props.values()) {
      if (!root.visible) continue
      root.updateMatrixWorld(true)
      const bounds = new THREE.Box3().setFromObject(root)
      if (!Number.isFinite(bounds.min.y) || bounds.min.y >= surfaceY) continue
      root.position.y += surfaceY - bounds.min.y
      root.updateMatrixWorld(true)
    }
  }

  dispose(): void {
    this.seabedRelief?.dispose()
    this.terrainTextures.forEach(texture => texture.dispose())
    this.terrainTextures = []
    this.backdropBatch.dispose()
    this.environment.dispose()
    this.root.removeFromParent()
    this.root.clear()
    for (const geometry of this.ownedGeometries) geometry.dispose()
    for (const material of this.ownedMaterials) material.dispose()
    this.ownedGeometries.clear()
    this.ownedMaterials.clear()
    this.materialCache.clear()
    this.props.clear()
    this.ambientBackdropProps.length = 0
  }

  private ownGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.ownedGeometries.add(geometry)
    return geometry
  }

  private ownMaterial<T extends THREE.Material>(material: T): T {
    this.ownedMaterials.add(material)
    return material
  }

  private addMesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    parent: THREE.Object3D = this.root,
  ): THREE.Mesh {
    const mesh = new THREE.Mesh(this.ownGeometry(geometry), material)
    mesh.receiveShadow = true
    mesh.castShadow = true
    parent.add(mesh)
    return mesh
  }

  private standard(color: number, roughness = 0.62, emissive = 0x000000, emissiveIntensity = 0): THREE.MeshStandardMaterial {
    const key = `standard:${color}:${roughness}:${emissive}:${emissiveIntensity}`
    const cached = this.materialCache.get(key) as THREE.MeshStandardMaterial | undefined
    if (cached) return cached
    const material = this.ownMaterial(new THREE.MeshStandardMaterial({ color, roughness, emissive, emissiveIntensity }))
    this.materialCache.set(key, material)
    return material
  }

  private terrainMaterial(kind: 'sand' | 'limestone', tint?: number): THREE.MeshStandardMaterial {
    const key = `terrain:${kind}:${tint}`
    const cached = this.materialCache.get(key) as THREE.MeshStandardMaterial | undefined
    if (cached) return cached
    const authored = cloneDioramaTerrainMaterial(kind, tint)
    const material = authored?.material ?? new THREE.MeshStandardMaterial({
      color: tint ?? (kind === 'sand' ? dioramaArt(this.sceneId).ground : 0x92aaa8), roughness: 0.94,
    })
    if (authored) this.terrainTextures.push(...authored.textures)
    this.ownMaterial(material)
    this.materialCache.set(key, material)
    return material
  }

  private physical(color: number, opacity: number): THREE.MeshPhysicalMaterial {
    const key = `physical:${color}:${opacity}`
    const cached = this.materialCache.get(key) as THREE.MeshPhysicalMaterial | undefined
    if (cached) return cached
    const material = this.ownMaterial(new THREE.MeshPhysicalMaterial({
      color,
      roughness: 0.16,
      transparent: true,
      opacity,
      thickness: 0.35,
      clearcoat: 0.75,
    }))
    this.materialCache.set(key, material)
    return material
  }

  private buildPlatform(): void {
    // A continuous seabed extends beyond every authored camera, including portrait
    // and overhead shots. The acting corridor stays at its original elevation.
    const geometry = new THREE.PlaneGeometry(240, 240, 160, 160)
    geometry.rotateX(-Math.PI / 2)
    const positions = geometry.attributes.position
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i), z = positions.getZ(i)
      const outsideLane = THREE.MathUtils.smoothstep(Math.abs(x), 5.5, 12)
      const distant = THREE.MathUtils.smoothstep(Math.abs(z), 5, 18)
      const dune = (Math.sin(x * 0.24 + z * 0.13) * 0.38 +
        Math.sin(z * 0.36 - x * 0.12) * 0.22) * Math.max(outsideLane, distant)
      let height = -0.035 + dune
      if (this.sceneId === 'ship') height -= 3.2
      if (this.sceneId === 'bubblebridge') {
        // A real channel cuts through the continuous banks; there are no outer
        // cylindrical bank walls to expose when the camera looks across the gap.
        height -= 3.2 * (1 - THREE.MathUtils.smoothstep(Math.abs(x), 3.7, 4.6))
      }
      positions.setY(i, height)
    }
    geometry.computeVertexNormals()
    const sand = this.terrainMaterial('sand', 0xe2dac4)
    // Unit tests construct sets in a DOM-less Node runtime. Keep the browser
    // caustic map for the rendered scene while using an inert texture there so
    // the authored terrain remains constructible and disposable in tests.
    const caustics = typeof document === 'undefined'
      ? new THREE.Texture()
      : new THREE.TextureLoader().load(`${import.meta.env.BASE_URL}textures/aaa/caustics.png`)
    caustics.wrapS = caustics.wrapT = THREE.RepeatWrapping
    this.terrainTextures.push(caustics)
    // Project the authored light plate in world space, so ripples continue across
    // the dune slopes and move independently from the grain of the sand.
    sand.onBeforeCompile = (shader) => {
      shader.uniforms.reefCaustics = { value: caustics }
      shader.uniforms.reefTime = this.causticTime
      shader.vertexShader = `varying vec3 reefWorldPosition;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nreefWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;')
      shader.fragmentShader = `varying vec3 reefWorldPosition; uniform sampler2D reefCaustics; uniform float reefTime;\n${shader.fragmentShader}`
        .replace('#include <color_fragment>', `#include <color_fragment>
          vec2 waterUv = reefWorldPosition.xz * 0.115;
          float waterA = texture2D(reefCaustics, waterUv + vec2(reefTime * 0.009, reefTime * 0.005)).r;
          float waterB = texture2D(reefCaustics, waterUv * 1.27 + vec2(-reefTime * 0.006, reefTime * 0.008)).r;
          float causticLight = smoothstep(0.28, 0.88, max(waterA, waterB));
          diffuseColor.rgb *= 0.78 + causticLight * 1.25;
          diffuseColor.rgb += vec3(0.08, 0.18, 0.20) * causticLight;`)
    }
    sand.customProgramCacheKey = () => 'continuous-ocean-caustics-v2'
    const floor = this.addMesh(projectDioramaTerrainUVs(geometry), sand)
    floor.name = 'ContinuousUnderwaterSeabed'
    floor.castShadow = false
    floor.receiveShadow = true
  }

  private buildFloorScatter(): THREE.InstancedMesh {
    const geometry = this.ownGeometry(projectDioramaTerrainUVs(fittedDioramaRock(3, new THREE.DodecahedronGeometry(0.12, 0)), 1.6))
    const material = this.terrainMaterial('limestone')
    const scatter = new THREE.InstancedMesh(geometry, material, FLOOR_SCATTER_COUNT)
    scatter.name = 'DioramaFloorScatter'
    const dummy = new THREE.Object3D()
    const color = new THREE.Color()
    for (let index = 0; index < FLOOR_SCATTER_COUNT; index++) {
      const angle = index * 2.399963
      const radius = 2.2 + ((index * 29) % 131) / 10
      dummy.position.set(Math.cos(angle) * radius, 0.075, Math.sin(angle) * radius * 0.85 - 2.4)
      if (this.sceneId === 'bubblebridge' && Math.abs(dummy.position.x) < 4.1) {
        dummy.position.x = (index % 2 ? 1 : -1) * (4.3 + (index % 5) * 0.5)
      }
      if (this.sceneId === 'ship') dummy.position.y = -3.18
      dummy.rotation.set(index * 0.17, angle, index * 0.11)
      dummy.scale.set(0.65 + (index % 4) * 0.18, 0.42 + (index % 3) * 0.16, 0.7 + (index % 5) * 0.12)
      dummy.updateMatrix()
      scatter.setMatrixAt(index, dummy.matrix)
      // Sediment chips inherit limestone hues; bright theme colors read as loose pickups.
      color.setHex(index % 3 === 0 ? 0xa99c7c : index % 3 === 1 ? 0x73847b : 0xc6b999)
      scatter.setColorAt(index, color)
    }
    scatter.instanceMatrix.needsUpdate = true
    if (scatter.instanceColor) scatter.instanceColor.needsUpdate = true
    this.root.add(scatter)
    return scatter
  }

  private buildBackdrop(): void {
    // Three-dimensional architecture and the scene's fog establish depth. No
    // camera-facing cards: they exposed edges and flattened every location.
    const kit = this.theme.propKit
    for (let index = 0; index < 14; index++) {
      const prop = buildThemeProp(kit, SCENE_LEVEL[this.sceneId] * 37 + index, this.theme.accent, index % 3 !== 0)
      this.trackFactoryGeometry(prop)
      const angle = (index / 14) * Math.PI * 2
      const radius = index % 2 ? 7.2 : 5.8
      prop.position.set(Math.cos(angle) * radius, index % 3 === 0 ? -0.02 : 0, -2.8 + Math.sin(angle) * 2)
      prop.rotation.y = -angle + Math.PI / 2
      prop.scale.multiplyScalar(index % 3 === 0 ? 1.35 : 0.85)
      this.root.add(prop)
      this.ambientBackdropProps.push(prop)
    }
    this.backdropBatch = new DioramaStaticBatch(this.ambientBackdropProps)
    this.root.add(this.backdropBatch.root)
  }

  private buildBackdropRelief(): THREE.InstancedMesh {
    const geometry = this.ownGeometry(projectDioramaTerrainUVs(fittedDioramaRock(0, new THREE.IcosahedronGeometry(0.38, 1)), 1.6))
    const material = this.terrainMaterial('limestone')
    const relief = new THREE.InstancedMesh(geometry, material, BACKDROP_RELIEF_COUNT)
    relief.name = 'DioramaBackdropRelief'
    const dummy = new THREE.Object3D()
    const color = new THREE.Color()
    for (let index = 0; index < BACKDROP_RELIEF_COUNT; index++) {
      const side = index % 2 === 0 ? -1 : 1
      const lane = Math.floor(index / 2)
      const x = side * (3.3 + (lane % 7) * 0.72)
      const z = -4.65 + (lane % 4) * 0.42
      const heightScale = 0.72 + (index % 5) * 0.2
      dummy.position.set(x, 0.46 * heightScale, z)
      dummy.rotation.set(0, index * 0.71, side * 0.08)
      dummy.scale.set(0.72 + (index % 3) * 0.2, heightScale, 0.72 + (index % 4) * 0.12)
      dummy.updateMatrix()
      relief.setMatrixAt(index, dummy.matrix)
      color.setHex(0xffffff).lerp(new THREE.Color(this.theme.accent), index % 3 === 0 ? 0.16 : 0.08)
      relief.setColorAt(index, color)
    }
    relief.instanceMatrix.needsUpdate = true
    if (relief.instanceColor) relief.instanceColor.needsUpdate = true
    this.root.add(relief)
    return relief
  }

  private buildLocation(): void {
    this.environment = new DioramaEnvironment(this.sceneId)
    this.root.add(this.environment.root)
    if (this.sceneId === 'bubblebridge') this.locationProps.add('bridge')
    if (this.sceneId === 'ship') this.locationProps.add('ship')
    if (this.sceneId === 'throne') this.locationProps.add('throne')
    if (this.sceneId === 'deepsea') this.locationProps.add('clam')
  }

  private buildBubbles(): THREE.InstancedMesh {
    const geometry = this.ownGeometry(new THREE.SphereGeometry(0.12, 8, 6))
    const material = this.ownMaterial(new THREE.MeshBasicMaterial({ color: 0xcff7ff, transparent: true, opacity: 0.075, depthWrite: false }))
    const bubbles = new THREE.InstancedMesh(geometry, material, DIORAMA_HIGH_DETAIL_BUDGET.bubbles)
    bubbles.name = 'DioramaBubbleField'
    const dummy = new THREE.Object3D()
    for (let index = 0; index < DIORAMA_HIGH_DETAIL_BUDGET.bubbles; index++) {
      dummy.position.set(-8 + ((index * 47) % 160) / 10, 0.35 + ((index * 31) % 68) / 10, -5 + (index % 9) * 0.62)
      dummy.scale.setScalar(0.2 + (index % 7) * 0.07)
      dummy.updateMatrix()
      bubbles.setMatrixAt(index, dummy.matrix)
    }
    bubbles.instanceMatrix.needsUpdate = true
    this.root.add(bubbles)
    return bubbles
  }

  private buildActionRing(): THREE.Mesh {
    const ring = this.addMesh(
      new THREE.TorusGeometry(3.8, 0.055, 6, 64),
      this.ownMaterial(new THREE.MeshBasicMaterial({ color: this.theme.accent, transparent: true, opacity: 0.68 })),
    )
    ring.rotation.x = Math.PI / 2
    ring.position.y = 0.12
    return ring
  }

  private addProp(prop: DioramaProp): void {
    let root: THREE.Object3D
    switch (prop) {
      case 'coin-chest': root = this.createChest(); break
      case 'coins': root = this.createCoins(); break
      case 'coin-bag': root = this.createCoinBag(); break
      case 'jelly-drop': root = this.createJellyDrop(); break
      case 'sandwich': root = buildKrabbyPatty(); this.trackFactoryGeometry(root); break
      case 'sticky-sandwich': root = this.createSandwich(0x86f7d2, 1); break
      case 'sour-sandwich': root = this.createSandwich(0xb92e69, 1); break
      case 'deepsea-sandwich': root = this.createSandwich(0x51d8ff, 1.08); break
      case 'giant-sandwich': root = this.createSandwich(0xff8fd4, 2.1); break
      case 'ultimate-sandwich': root = this.createSandwich(0xffffff, 1.35, true); break
      case 'jelly-jars': root = this.createJellyJars(); break
      case 'bridge': root = this.createBridge(); break
      case 'ship': root = this.createShip(); break
      case 'clam': root = this.createClam(); break
      case 'map': root = this.createMap(); break
      case 'patty': root = buildKrabbyPatty(); this.trackFactoryGeometry(root); break
      case 'throne': root = this.createThrone(); break
      case 'sign': root = this.createSign(); break
      case 'web': root = this.createWeb(); break
      case 'lightning': root = this.createLightningProp(); break
      case 'decorations': root = this.createDecorations(); break
    }
    root.name = `DioramaProp_${prop}`
    root.position.copy(this.propPosition(prop))
    this.root.add(root)
    this.props.set(prop, { root, rests: captureRest(root) })
  }

  private propPosition(prop: DioramaProp): THREE.Vector3 {
    switch (prop) {
      case 'coin-chest': return new THREE.Vector3(0, 0.25, -0.7)
      case 'coins': return new THREE.Vector3(0, 0.35, -0.75)
      case 'coin-bag': return new THREE.Vector3(0, 0.25, -0.4)
      case 'jelly-drop': return new THREE.Vector3(0, 1.1, -0.2)
      case 'sandwich': return new THREE.Vector3(0, 1.15, 0.15)
      case 'sticky-sandwich':
      case 'sour-sandwich':
      case 'deepsea-sandwich':
      case 'ultimate-sandwich': return new THREE.Vector3(0, 1.15, 0.15)
      case 'giant-sandwich': return new THREE.Vector3(0, 1.3, -0.5)
      case 'jelly-jars': return new THREE.Vector3(0, 0.35, -0.55)
      case 'bridge': return new THREE.Vector3(0, 0, 0)
      case 'ship': return new THREE.Vector3(0, 0, -1.7)
      case 'clam': return new THREE.Vector3(0, 0.3, -0.75)
      case 'map': return new THREE.Vector3(0, 1.2, -0.2)
      case 'patty': return new THREE.Vector3(0, 1.2, 0)
      case 'throne': return new THREE.Vector3(0, 0, -3.1)
      case 'sign': return new THREE.Vector3(0, 0.15, -0.6)
      case 'web': return new THREE.Vector3(0, 1.7, -1.2)
      case 'lightning': return new THREE.Vector3(0, 1.4, -0.8)
      case 'decorations': return new THREE.Vector3(0, 2.2, -1.2)
    }
  }

  private createChest(): THREE.Group {
    const group = new THREE.Group()
    const wood = this.standard(0x8a522f, 0.74)
    const gold = this.standard(0xffc93b, 0.3, 0xffa800, 0.2)
    const base = this.addMesh(new RoundedBoxGeometry(1.65, 0.85, 1.05, 3, 0.12), wood, group)
    base.position.y = 0.43
    const lid = this.addMesh(new RoundedBoxGeometry(1.68, 0.46, 1.08, 3, 0.15), wood, group)
    lid.name = 'ChestLid'
    lid.position.set(0, 0.98, -0.25)
    for (const x of [-0.58, 0, 0.58]) {
      const band = this.addMesh(new THREE.BoxGeometry(0.1, 1.25, 1.1), gold, group)
      band.position.set(x, 0.62, 0)
    }
    const coinGeo = this.ownGeometry(new THREE.CylinderGeometry(0.12, 0.12, 0.045, 12))
    for (let index = 0; index < 18; index++) {
      const coin = new THREE.Mesh(coinGeo, gold)
      coin.name = `BurstCoin_${index}`
      coin.rotation.x = Math.PI / 2
      coin.position.set((index % 6 - 2.5) * 0.18, 0.9 + Math.floor(index / 6) * 0.08, -0.05)
      group.add(coin)
    }
    return group
  }

  private createCoins(): THREE.Group {
    const group = new THREE.Group()
    const material = this.standard(0xffcb3d, 0.25, 0xffae00, 0.24)
    const geometry = this.ownGeometry(new THREE.CylinderGeometry(0.13, 0.13, 0.05, 14))
    for (let index = 0; index < 24; index++) {
      const coin = new THREE.Mesh(geometry, material)
      coin.name = `Coin_${index}`
      coin.rotation.x = Math.PI / 2
      coin.position.set((index % 8 - 3.5) * 0.25, Math.floor(index / 8) * 0.12, (index % 3) * 0.16)
      group.add(coin)
    }
    return group
  }

  private createCoinBag(): THREE.Group {
    const group = new THREE.Group()
    const sack = this.addMesh(new THREE.SphereGeometry(0.48, 16, 12), this.standard(0x17384a, 0.82), group)
    sack.scale.set(0.88, 1.12, 0.72)
    sack.position.y = 0.46
    const tie = this.addMesh(new THREE.TorusGeometry(0.2, 0.045, 7, 14), this.standard(0xffcb3d, 0.3), group)
    tie.rotation.x = Math.PI / 2
    tie.position.y = 0.84
    return group
  }

  private createJellyDrop(): THREE.Group {
    const group = new THREE.Group()
    const drop = this.addMesh(new THREE.SphereGeometry(0.28, 16, 12), this.physical(0xff69b4, 0.82), group)
    drop.scale.set(0.8, 1.25, 0.8)
    const tip = this.addMesh(new THREE.ConeGeometry(0.14, 0.32, 12), drop.material as THREE.Material, group)
    tip.position.y = 0.3
    return group
  }

  private createSandwich(jellyColor: number, scale = 1, prismatic = false): THREE.Group {
    const group = new THREE.Group()
    const bread = this.standard(0xf2c97f, 0.78)
    const jelly = this.physical(jellyColor, 0.84)
    const lower = this.addMesh(new RoundedBoxGeometry(1.05, 0.22, 0.82, 3, 0.12), bread, group)
    lower.position.y = 0.12
    const filling = this.addMesh(new RoundedBoxGeometry(1.12, 0.18, 0.88, 3, 0.1), jelly, group)
    filling.name = 'SandwichJelly'
    filling.position.y = 0.34
    const upper = this.addMesh(new RoundedBoxGeometry(1.05, 0.22, 0.82, 3, 0.12), bread, group)
    upper.position.y = 0.56
    if (prismatic) {
      const colors = [0xffcf4a, 0xa968ff, 0x5be8a5, 0x8cecff, 0xff5fa2]
      for (let index = 0; index < colors.length; index++) {
        const ribbon = this.addMesh(
          new THREE.TorusGeometry(0.5 + index * 0.055, 0.025, 5, 24),
          this.standard(colors[index], 0.22, colors[index], 1.2),
          group,
        )
        ribbon.rotation.x = Math.PI / 2
        ribbon.position.y = 0.36 + (index - 2) * 0.022
      }
    }
    group.scale.setScalar(scale)
    return group
  }

  private createJellyJars(): THREE.Group {
    const group = new THREE.Group()
    const colors = [0xffc94e, 0xa45cff, 0x5ee297, 0xc8f7ff, 0xe64e71, 0x45c8ff, 0xff75c8, 0xf7fbff, 0xe5b3ff]
    colors.forEach((color, index) => {
      const jar = new THREE.Group()
      const glass = this.addMesh(new THREE.CylinderGeometry(0.16, 0.19, 0.52, 12), this.physical(0xdffaff, 0.38), jar)
      glass.position.y = 0.26
      const sample = this.addMesh(new THREE.SphereGeometry(0.13, 10, 8), this.standard(color, 0.24, color, 1.1), jar)
      sample.position.y = 0.2
      const lid = this.addMesh(new THREE.CylinderGeometry(0.18, 0.18, 0.08, 12), this.standard(0xd9b267, 0.45), jar)
      lid.position.y = 0.56
      jar.position.set((index % 5 - 2) * 0.44, 0, Math.floor(index / 5) * 0.45)
      group.add(jar)
    })
    return group
  }

  private createSign(): THREE.Group {
    const group = new THREE.Group()
    const wood = this.standard(0x9a653d, 0.86)
    const post = this.addMesh(new THREE.CylinderGeometry(0.09, 0.12, 2.3, 10), wood, group)
    post.position.y = 1.15
    for (let index = 0; index < 3; index++) {
      const arrow = this.addMesh(new RoundedBoxGeometry(1.6, 0.34, 0.15, 2, 0.05), wood, group)
      arrow.name = `MazeSign_${index}`
      arrow.position.set(index % 2 ? -0.42 : 0.42, 1.82 - index * 0.42, 0)
      arrow.rotation.y = index % 2 ? Math.PI : 0
    }
    return group
  }

  private createWeb(): THREE.Group {
    const group = new THREE.Group()
    const colors = [0xff5869, 0x55b8ff, 0xffe85a]
    for (let index = 0; index < 7; index++) {
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(-2.4, (index - 3) * 0.44, 0),
        new THREE.Vector3(-0.7, (index - 3) * 0.26 + Math.sin(index) * 0.35, 0.1),
        new THREE.Vector3(0.8, (index - 3) * 0.34 - Math.cos(index) * 0.28, 0),
        new THREE.Vector3(2.4, (index - 3) * 0.44, 0),
      ])
      const strand = this.addMesh(
        new THREE.TubeGeometry(curve, 18, 0.035, 5, false),
        this.standard(colors[index % colors.length], 0.2, colors[index % colors.length], 1.6),
        group,
      )
      strand.name = `ElectricWeb_${index}`
    }
    return group
  }

  private createLightningProp(): THREE.Group {
    const group = new THREE.Group()
    const glow = this.standard(0xd9fbff, 0.16, 0x6beaff, 2.4)
    for (let index = 0; index < 4; index++) {
      const x = (index - 1.5) * 0.55
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(x, 1.7, 0),
        new THREE.Vector3(x + 0.28, 0.8, 0.08),
        new THREE.Vector3(x - 0.2, 0.1, 0),
        new THREE.Vector3(x + 0.18, -0.7, 0.06),
      ])
      this.addMesh(new THREE.TubeGeometry(curve, 10, 0.045, 5, false), glow, group)
    }
    return group
  }

  private createDecorations(): THREE.Group {
    const group = new THREE.Group()
    const cable = this.addMesh(new THREE.TorusGeometry(2.9, 0.035, 5, 48, Math.PI), this.standard(0x25465a, 0.65), group)
    cable.rotation.z = Math.PI
    for (let index = 0; index < 12; index++) {
      const color = index % 3 === 0 ? 0xffd34e : index % 3 === 1 ? 0x68e9ff : 0xff78c8
      const bulb = this.addMesh(new THREE.SphereGeometry(0.11, 10, 8), this.standard(color, 0.24, color, 1.6), group)
      const angle = (index / 11) * Math.PI
      bulb.position.set(Math.cos(angle) * 2.9, Math.sin(angle) * 2.9, 0)
    }
    return group
  }

  private createBridge(): THREE.Group {
    const group = new THREE.Group()
    const material = this.physical(0xbef5ff, 0.56)
    for (let index = 0; index < 9; index++) {
      const bubble = this.addMesh(new THREE.SphereGeometry(0.55, 16, 12), material, group)
      bubble.name = `BridgeBubble_${index}`
      bubble.position.set(-4 + index, -0.37 + Math.sin(index * 0.7) * 0.035, 0)
      bubble.scale.set(1, 0.72, 2.2)
    }
    return group
  }

  private createShip(): THREE.Group {
    const group = new THREE.Group()
    const wood = this.standard(0x68462f, 0.9)
    const metal = this.standard(0x50616b, 0.52)
    // Open-front wreck: separate side gangways and stern strip surround a
    // genuine opening. No solid plane behind the decorative hatch ring.
    for (const [x, z, width, depth] of [[0, -1.65, 8.4, 1.05], [-3.35, 0.5, 1.65, 3.2], [3.35, 0.5, 1.65, 3.2]]) {
      const deck = this.addMesh(new RoundedBoxGeometry(width, 0.22, depth, 2, 0.08), wood, group)
      deck.name = 'ShipCutawayGangway'
      deck.position.set(x, -0.08, z)
      for (let row = 0; row < Math.ceil(depth / 0.32); row++) {
        const seam = this.addMesh(new THREE.BoxGeometry(width, 0.015, 0.025), metal, group)
        seam.position.set(x, 0.04, z - depth / 2 + row * 0.32)
      }
    }
    for (const x of [-2.5, 2.5]) {
      const edge = this.addMesh(new THREE.BoxGeometry(0.12, 0.3, 3.2), metal, group)
      edge.position.set(x, -0.08, 0.5)
    }
    for (const x of [-3.4, 3.4]) {
      const barrel = this.addMesh(new THREE.CylinderGeometry(0.42, 0.42, 0.95, 14), wood, group)
      barrel.position.set(x, 0.5, -1.5)
    }
    return group
  }

  private createClam(): THREE.Group {
    const group = new THREE.Group()
    const material = this.standard(0x9ca9bd, 0.52)
    const lower = this.addMesh(new THREE.SphereGeometry(0.85, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2), material, group)
    lower.name = 'ClamLower'
    lower.scale.set(1.2, 0.35, 1)
    lower.rotation.x = Math.PI
    const upper = new THREE.Group()
    upper.name = 'ClamUpper'
    upper.position.z = -0.8
    group.add(upper)
    const shell = this.addMesh(new THREE.SphereGeometry(0.85, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), material, upper)
    shell.scale.set(1.2, 0.35, 1)
    shell.position.z = 0.8
    const rim = this.addMesh(new THREE.TorusGeometry(0.85, 0.045, 6, 32), this.standard(0xc9b7af, 0.65), upper)
    rim.rotation.x = Math.PI / 2
    rim.scale.x = 1.2
    rim.position.z = 0.8
    return group
  }

  private createMap(): THREE.Group {
    const group = new THREE.Group()
    this.addMesh(new RoundedBoxGeometry(1.35, 0.88, 0.06, 2, 0.08), this.standard(0xf4d89f, 0.9), group)
    const route = this.addMesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
      new THREE.Vector3(-0.45, -0.2, 0.05),
      new THREE.Vector3(-0.1, 0.24, 0.05),
      new THREE.Vector3(0.2, -0.04, 0.05),
      new THREE.Vector3(0.48, 0.2, 0.05),
    ]), 10, 0.025, 5, false), this.standard(0xd84949, 0.55), group)
    route.position.z = 0.05
    group.rotation.x = -0.08
    return group
  }

  private createThrone(): THREE.Group {
    const group = new THREE.Group()
    const gold = this.standard(0xffc83d, 0.28, 0xff9e00, 0.22)
    const purple = this.standard(0x7138a8, 0.42, 0x7d32cc, 0.2)
    const seat = this.addMesh(new RoundedBoxGeometry(2.6, 0.85, 1.5, 3, 0.18), purple, group)
    seat.position.y = 0.6
    const back = this.addMesh(new RoundedBoxGeometry(3.15, 4.2, 0.7, 3, 0.22), purple, group)
    back.position.set(0, 2.55, -0.55)
    for (const x of [-1.35, 1.35]) {
      const post = this.addMesh(new THREE.CylinderGeometry(0.22, 0.28, 4.7, 12), gold, group)
      post.position.set(x, 2.45, -0.35)
      const jewel = this.addMesh(new THREE.OctahedronGeometry(0.38, 0), this.physical(0xff69b4, 0.82), group)
      jewel.position.set(x, 4.85, -0.35)
    }
    return group
  }

  private applyAction(
    cue: ActionCue,
    progress: number,
    elapsed: number,
    actorPosition: (character: Character) => THREE.Vector3,
  ): void {
    if (
      cue.type === 'trap' || cue.type === 'threaten' || cue.type === 'break-spell' ||
      cue.type === 'command-pulse' || cue.type === 'sever-command' || cue.type === 'surround' ||
      cue.type === 'summon-wave'
    ) {
      this.actionRing.visible = true
      const releases = cue.type === 'break-spell' || cue.type === 'sever-command'
      this.actionRing.scale.setScalar(releases ? 1 + progress * 1.8 : 0.72 + Math.sin(elapsed * 5) * 0.08)
      ;(this.actionRing.material as THREE.Material).opacity = releases ? 0.8 * (1 - progress) : 0.62
      this.actionRing.rotation.z = elapsed * (releases ? -1.5 : 0.55)
    }
    if (!cue.prop) return
    const rig = this.props.get(cue.prop)
    if (!rig) return
    const prop = rig.root
    // A revealed/opened wreck remains a full-size location, not a spinning pickup.
    if (cue.prop === 'ship' && ['discover', 'reveal', 'open'].includes(cue.type)) return
    const actor = actorPosition(cue.actor)
    const target = cue.target ? actorPosition(cue.target) : new THREE.Vector3(0, 1.3, 0)
    const p = ease(progress)
    const arc = Math.sin(p * Math.PI)

    switch (cue.type) {
      case 'spill': {
        prop.position.x = actor.x + 0.85
        prop.position.z = actor.z + 0.75
        prop.rotation.z -= p * 1.15
        prop.position.x += p * 2.35
        prop.position.y += arc * 0.9
        let index = 0
        prop.traverse((child) => {
          if (!child.name.startsWith('BurstCoin_')) return
          const angle = (index / 18) * Math.PI * 2
          child.position.x += Math.cos(angle) * p * (1.8 + (index % 4) * 0.42)
          child.position.y += arc * (1.45 + (index % 3) * 0.55)
          child.position.z += Math.sin(angle) * p * 1.75
          child.rotation.y += p * Math.PI * (index % 2 ? 3 : -3)
          index++
        })
        break
      }
      case 'knock-over':
        prop.rotation.z -= p * 1.05
        prop.position.x += p * 0.8
        break
      case 'roll':
        prop.position.x += (p - 0.5) * 5
        prop.rotation.z -= p * Math.PI * 4
        break
      case 'bounce':
        prop.position.x += p * 2.4
        prop.position.y += Math.abs(Math.sin(p * Math.PI * 2)) * 1.8
        prop.rotation.z += p * Math.PI * 2.5
        break
      case 'burst-open': {
        const lid = prop.getObjectByName('ChestLid')
        if (lid) lid.rotation.x -= p * 1.65
        prop.scale.multiplyScalar(1 + arc * 0.18)
        let index = 0
        prop.traverse((child) => {
          if (!child.name.startsWith('BurstCoin_')) return
          const angle = (index / 18) * Math.PI * 2
          child.position.x += Math.cos(angle) * p * 2.5
          child.position.y += arc * (1.2 + (index % 4) * 0.35)
          child.position.z += Math.sin(angle) * p * 1.6
          index++
        })
        break
      }
      case 'open': {
        const lid = prop.getObjectByName('ChestLid') ?? prop.getObjectByName('ClamUpper')
        if (lid) lid.rotation.x -= p * 1.25
        prop.scale.multiplyScalar(0.88 + arc * 0.16)
        break
      }
      case 'collect':
      case 'steal':
      case 'swarm-carry':
      case 'catch':
      case 'hug-prop':
      case 'anchor': {
        if (cue.type === 'anchor' && cue.prop === 'ship') break
        if (cue.prop === 'jelly-jars' && cue.type === 'collect') {
          // Bob is collecting the jars from beside the counter. Keep them on
          // the counter-side of his body instead of lerping through the
          // satchel and leaving Krabs to collide with the whole rack.
          prop.position.copy(actor).add(new THREE.Vector3(2.2, 0.6, -0.15))
          prop.scale.multiplyScalar(1 - p * 0.12)
          break
        }
        prop.position.lerpVectors(prop.position, actor.clone().add(new THREE.Vector3(0, 1.35, 0.4)), p)
        if (cue.type !== 'hug-prop' && cue.type !== 'anchor') prop.scale.multiplyScalar(1 - p * 0.35)
        prop.rotation.y += elapsed * 1.8
        break
      }
      case 'offer':
      case 'reward':
      case 'offer-coin':
      case 'serve': {
        prop.position.lerpVectors(actor.clone().add(new THREE.Vector3(0, 1.2, 0.35)), target.clone().add(new THREE.Vector3(0, 1.25, 0.35)), p)
        prop.position.y += arc * 0.55
        prop.rotation.y += elapsed * 1.3
        break
      }
      case 'throw': {
        prop.position.lerpVectors(actor.clone().add(new THREE.Vector3(0, 1.45, 0.4)), target.clone().add(new THREE.Vector3(0, 1.25, 0.35)), p)
        prop.position.y += arc * 2.15
        prop.rotation.x += p * Math.PI * 4
        break
      }
      case 'drop':
      case 'spit-out': {
        prop.position.copy(actor).add(new THREE.Vector3(0, 1.5 - p * 1.35, 0.45))
        prop.position.x += (p - 0.5) * 1.4
        prop.rotation.z += p * Math.PI * 3
        break
      }
      case 'pull':
      case 'fight-over': {
        prop.position.lerpVectors(target, actor, 0.5 + Math.sin(elapsed * 6) * 0.28 * (1 - p * 0.35))
        prop.position.y += 1.1 + arc * 0.25
        break
      }
      case 'eat':
      case 'swallow': {
        prop.position.lerpVectors(prop.position, actor.clone().add(new THREE.Vector3(0, 1.5, 0.5)), p)
        prop.scale.multiplyScalar(Math.max(0.03, 1 - p))
        break
      }
      case 'discover':
      case 'reveal':
      case 'build':
      case 'project-map':
      case 'decorate': {
        prop.scale.multiplyScalar(0.12 + p * 0.88)
        if (cue.type === 'decorate') prop.position.copy(actor).add(new THREE.Vector3(0, 1.25, 0.45))
        prop.position.y += arc * 0.55
        prop.rotation.y += elapsed * 1.15
        break
      }
      case 'rotate-sign':
        prop.rotation.y += p * Math.PI
        prop.children.forEach((child, index) => { child.rotation.y += Math.sin(elapsed * 3 + index) * 0.18 })
        break
      case 'pin-sign':
        prop.rotation.z *= 1 - p
        prop.position.y += arc * 0.18
        break
      case 'repair': {
        let index = 0
        prop.traverse((child) => {
          if (!child.name.startsWith('BridgeBubble_')) return
          const local = clamp01(p * 1.7 - index * 0.1)
          child.scale.multiplyScalar(local)
          index++
        })
        break
      }
      case 'pop-bubble': {
        let index = 0
        prop.traverse((child) => {
          if (!child.name.startsWith('BridgeBubble_')) return
          if (index++ % 2 === 0) child.scale.multiplyScalar(Math.max(0.03, 1 - p * 1.4))
        })
        break
      }
      case 'form-bubble':
      case 'stabilize': {
        let index = 0
        prop.traverse((child) => {
          if (!child.name.startsWith('BridgeBubble_')) return
          const local = clamp01(p * 1.6 - index * 0.08)
          child.scale.multiplyScalar(cue.type === 'stabilize' ? 1 + Math.sin(elapsed * 2 + index) * 0.035 : local)
          index++
        })
        break
      }
      case 'tilt':
        prop.rotation.z += p * 0.38
        prop.position.y -= p * 0.25
        break
      case 'count':
        prop.rotation.y = Math.sin(elapsed * 0.8) * 0.08
        break
      case 'summon-lightning':
        prop.scale.setScalar(0.3 + p * 0.9 + Math.sin(elapsed * 20) * 0.08)
        prop.rotation.z += Math.sin(elapsed * 14) * 0.035
        break
      case 'sever-command':
      case 'break-spell':
        prop.scale.multiplyScalar(Math.max(0.02, 1 - p))
        prop.rotation.z += p * 0.8
        break
      case 'collapse-throne':
        prop.rotation.z += p * 0.3
        prop.position.y -= p * 1.1
        prop.children.forEach((child, index) => {
          child.position.x += (index % 2 ? 1 : -1) * p * (0.3 + index * 0.04)
          child.rotation.z += (index % 2 ? 1 : -1) * p * 0.6
        })
        break
    }
  }

  private trackFactoryGeometry(root: THREE.Object3D): void {
    root.traverse((object) => {
      const mesh = object as THREE.Mesh
      if (mesh.geometry) this.ownedGeometries.add(mesh.geometry)
    })
  }
}
